/**
 * Transforms a GoodsShipment GraphQL object to/from the form state.
 *
 * IMPORTANT: toSchema() must only output fields valid on the mutation input.
 * Do NOT spread the original — it contains federated/queried fields (like
 * importer.name, __typename) that the mutation rejects.
 *
 * @see LLD Section 3.2 — Transform Classes
 */
import { BaseTransformer } from './BaseTransformer';
import {
  GoodsShipmentFormState,
  PLACEHOLDER_DEFAULTS,
} from '../schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from './GoodsItemDetailTransformer';
import { getEditedGoodsItemsForShipment } from './goodsItemFormRef';
import { stripTypeNames } from '@xbcb/js-utils/dist';
import type {
  goodsShipmentFieldsFragment,
  GoodsShipmentInput,
  TransportContractDocumentType,
  BorderTransportMeansInput,
  TransportEquipmentInput,
} from '__generated__/graphql';

/** Use the codegen fragment type as the API data shape */
export type GoodsShipmentApiData = goodsShipmentFieldsFragment;

/*
 * Stamps shipment-level fields onto an individual item at save time.
 * Currency is always stamped (shipment currency is authoritative for all items).
 * Extensible: add new stamp logic here as shipment→item fields are identified.
 */
const stampShipmentFields = (
  item: Record<string, any>,
  formData: GoodsShipmentFormState,
): Record<string, any> => {
  const currency = formData.currency;
  if (!currency) return item;

  // Stamp currency onto customsValue (always — shipment currency is authoritative)
  if (item.customsValue?.value != null) {
    item.customsValue = { ...item.customsValue, currency };
  }

  // Stamp currency onto each valuationAdjustment amount
  if (item.valuationAdjustment) {
    item.valuationAdjustment = item.valuationAdjustment.map((a: any) =>
      a.amount ? { ...a, amount: { ...a.amount, currency } } : a,
    );
  }

  return item;
};

export class CuDeGoodsShipmentTransformer extends BaseTransformer<
  GoodsShipmentApiData,
  GoodsShipmentFormState,
  GoodsShipmentInput
> {
  toForm(source: GoodsShipmentApiData): GoodsShipmentFormState {
    const consignment = source.consignment?.[0];
    const doc = consignment?.transportContractDocument?.[0];

    return {
      transportDoc: {
        type: doc?.type ?? null,
        identification: doc?.identification ?? null,
      },
      ...PLACEHOLDER_DEFAULTS,
      incoterms: source.tradeTerms?.[0]?.condition ?? null,
      currency: source.goodsItem?.[0]?.customsValue?.currency ?? null,
      invoiceValue: consignment?.value ?? null,
      exportCountry: source.exportCountry?.[0]?.country ?? null,
      importCountry: source.importCountry?.[0]?.country ?? null,
      // No shipment-level valuationAdjustment field exists in the API yet.
      // Form-only state used to filter item-level adjustment code options.
      adjustments: [],
    };
  }

  toSchema(
    formData: GoodsShipmentFormState,
    original: GoodsShipmentApiData,
    shipmentIndex = 0,
  ): GoodsShipmentInput {
    const originalConsignment = original.consignment?.[0];

    return {
      tradeTerms: [{ condition: formData.incoterms }],
      exportCountry: [{ country: formData.exportCountry }],
      importCountry: [{ country: formData.importCountry }],
      consignment: [
        {
          value: formData.invoiceValue,
          transportContractDocument: [
            {
              type: formData.transportDoc.type as TransportContractDocumentType,
              identification: formData.transportDoc.identification,
            },
          ],
          // Preserve non-editable fields managed by Transportation tab.
          borderTransportationMean:
            originalConsignment?.borderTransportationMean
              ? (stripTypeNames(
                  originalConsignment.borderTransportationMean,
                ) as unknown as BorderTransportMeansInput[])
              : undefined,
          transportEquipment: originalConsignment?.transportEquipment
            ? (stripTypeNames(
                originalConsignment.transportEquipment,
              ) as unknown as TransportEquipmentInput[])
            : undefined,
        },
      ],
      importer: original.importer?.map((imp) => ({ id: imp.id })),
      goodsItem: (() => {
        const edited = getEditedGoodsItemsForShipment(shipmentIndex);
        const itemTransformer = new GoodsItemDetailTransformer();
        return original.goodsItem?.map((item) => {
          let result;
          const entry = edited.get(item.sequence!);
          if (entry) {
            result = {
              ...itemTransformer.toSchema(entry.formData, entry.original),
              seller: entry.original.seller
                ? { id: entry.original.seller.id }
                : undefined,
            };
          } else {
            // Unvisited: round-trip through transformer to emit only mutation-valid fields
            result = {
              ...itemTransformer.toSchema(itemTransformer.toForm(item), item),
              seller: item.seller ? { id: item.seller.id } : undefined,
            };
          }
          return stampShipmentFields(result, formData);
        });
      })(),
    };
  }
}
